import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faEnvelopes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inviteCampaignMemberByEmail } from 'api/campaigns';
import * as AddMemberCollapsible from 'assets/js/pages/campaign-settings/campaign-members/AddMemberCollapsible';
import { useAddMemberCollapsibleStore } from 'assets/js/pages/campaign-settings/campaign-members/useAddMemberCollapsibleStore';
import * as Form from 'components/form';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface InviteByEmailForm {
    emails: string;
}

const AddMemberInviteByEmailCollapsible = () => {
    const toast = useToast();
    const { collapsibleCard, setCollapsibleCard } = useAddMemberCollapsibleStore();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;
    const methods = Form.useForm<InviteByEmailForm>({
        defaultValues: {
            emails: '',
        },
    });
    const { path, control, handleSubmit } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: InviteByEmailForm) =>
            inviteCampaignMemberByEmail(campaignId, values.emails.split(/[,|\n]/)),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.invites(campaignId),
            });

            toast.success('Sent team member invites successfully!');
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to send team member invites');
        },
    });

    return (
        <Form.Provider {...methods}>
            <AddMemberCollapsible.Root
                open={collapsibleCard === 'email'}
                onOpenChange={() => setCollapsibleCard('email')}
            >
                <AddMemberCollapsible.Trigger>
                    <AddMemberCollapsible.TriggerLeft>
                        <FontAwesomeIcon
                            size={'lg'}
                            className={'text-blue-500'}
                            icon={faEnvelopes}
                        />
                    </AddMemberCollapsible.TriggerLeft>
                    <AddMemberCollapsible.TriggerRight>
                        <div className={'flex items-center justify-between'}>
                            <p className={'text-sm font-medium'}>Invite by email</p>
                            <AddMemberCollapsible.ExpandIcon />
                        </div>
                    </AddMemberCollapsible.TriggerRight>
                </AddMemberCollapsible.Trigger>
                <AddMemberCollapsible.Content>
                    <form
                        className={'contents'}
                        onSubmit={handleSubmit(submit as SubmitHandler<InviteByEmailForm>)}
                    >
                        <div className={'flex flex-col gap-4'}>
                            <Form.Container className={'gap-2'}>
                                <Form.Label name={path('emails')} className={'font-normal'}>
                                    Enter the email addresses of team members you'd like to invite
                                    below (separated by commas).
                                </Form.Label>
                                <Form.Textarea
                                    placeholder={'john.doe@example.com, jane.doe@example.com'}
                                    control={control}
                                    name={'emails'}
                                    rules={{ required: 'Email list is required' }}
                                />
                                <Form.Error name={path('emails')} />
                            </Form.Container>
                            <div>
                                <Form.SubmitButton>Send invites</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </AddMemberCollapsible.Content>
            </AddMemberCollapsible.Root>
        </Form.Provider>
    );
};

export { AddMemberInviteByEmailCollapsible };
